(() => {
  'use strict';

  const doc = document;
  const win = window;
  const body = doc.body;
  const root = doc.documentElement;
  const reduceMotion = win.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Hero footage is decorative. Respect reduced-motion preferences while keeping its poster visible.
  if (reduceMotion) {
    doc.querySelectorAll('[data-background-video]').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
    });
  }

  /* Header and mobile navigation */
  const header = doc.querySelector('[data-header]');
  const menuButton = doc.querySelector('[data-menu]');
  const mobileNav = doc.querySelector('[data-mobile]');
  let menuReturnFocus = null;

  const setMenu = (open) => {
    if (!menuButton || !mobileNav) return;
    menuButton.classList.toggle('is-open', open);
    mobileNav.classList.toggle('is-open', open);
    menuButton.setAttribute('aria-expanded', String(open));
    menuButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    mobileNav.setAttribute('aria-hidden', String(!open));
    mobileNav.inert = !open;
    body.classList.toggle('nav-open', open);
    if (open) {
      menuReturnFocus = doc.activeElement;
      const firstLink = mobileNav.querySelector('a');
      if (firstLink) firstLink.focus({ preventScroll: true });
    } else if (menuReturnFocus && menuReturnFocus.focus) {
      menuReturnFocus.focus({ preventScroll: true });
      menuReturnFocus = null;
    }
  };

  if (menuButton && mobileNav) {
    mobileNav.inert = true;
    menuButton.addEventListener('click', () => setMenu(!mobileNav.classList.contains('is-open')));
    mobileNav.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));
  }

  const updateHeader = () => {
    if (header) header.classList.toggle('is-scrolled', win.scrollY > 24);
  };
  win.addEventListener('scroll', updateHeader, { passive: true });
  updateHeader();

  /* Header search dialog: keeps search on the storefront until the query is submitted. */
  const searchShell = doc.querySelector('[data-search-shell]');
  const searchDialog = searchShell ? searchShell.querySelector('.ro-search-dialog') : null;
  const searchInput = searchShell ? searchShell.querySelector('[data-search-input]') : null;
  let searchReturnFocus = null;
  const setSearch = (open) => {
    if (!searchShell || !searchDialog) return;
    searchShell.classList.toggle('is-open', open);
    searchShell.setAttribute('aria-hidden', String(!open));
    searchDialog.inert = !open;
    body.classList.toggle('search-open', open);
    if (open) {
      searchReturnFocus = doc.activeElement;
      win.setTimeout(() => { if (searchInput) searchInput.focus({ preventScroll: true }); }, 40);
    } else if (searchReturnFocus && searchReturnFocus.focus) {
      searchReturnFocus.focus({ preventScroll: true });
      searchReturnFocus = null;
    }
  };
  if (searchShell && searchDialog) {
    searchDialog.inert = true;
    doc.querySelectorAll('[data-search-open]').forEach((button) => button.addEventListener('click', () => {
      if (mobileNav && mobileNav.classList.contains('is-open')) setMenu(false);
      setSearch(true);
    }));
    searchShell.querySelectorAll('[data-search-close]').forEach((button) => button.addEventListener('click', () => setSearch(false)));
    searchDialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const nodes = Array.from(searchDialog.querySelectorAll('input:not([disabled]), button:not([disabled]), a[href]')).filter((node) => !node.hidden);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && doc.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && doc.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }

  /* Cart drawer, including AJAX add-to-cart updates */
  const cartShell = doc.querySelector('[data-cart-shell]');
  const cartDrawer = doc.querySelector('[data-cart]');
  const cartBody = doc.querySelector('[data-cart-body]');
  const cartTotal = doc.querySelector('[data-cart-total]');
  const cartCountText = doc.querySelector('[data-cart-count]');
  const headerCount = doc.querySelector('[data-header-cart-count]');
  let cartReturnFocus = null;

  const formatMoney = (cents, currency) => {
    const format = body.dataset.moneyFormat;
    if (win.Shopify && typeof win.Shopify.formatMoney === 'function' && format) {
      return win.Shopify.formatMoney(cents, format);
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || body.dataset.storeCurrency || 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return ((Number(cents) || 0) / 100).toFixed(2);
    }
  };

  const setCart = (open) => {
    if (!cartShell || !cartDrawer) return;
    cartShell.classList.toggle('is-open', open);
    cartDrawer.classList.toggle('is-open', open);
    cartShell.setAttribute('aria-hidden', String(!open));
    cartDrawer.inert = !open;
    body.classList.toggle('cart-open', open);
    if (open) {
      cartReturnFocus = doc.activeElement;
      const focusable = cartDrawer.querySelector('button:not([disabled]), a[href], input:not([disabled])');
      if (focusable) focusable.focus({ preventScroll: true });
    } else if (cartReturnFocus && cartReturnFocus.focus) {
      cartReturnFocus.focus({ preventScroll: true });
      cartReturnFocus = null;
    }
  };

  const createCartLine = (item, index) => {
    const article = doc.createElement('article');
    article.className = 'ro-cart-line';

    const link = doc.createElement('a');
    link.href = item.url || '/cart';
    link.setAttribute('aria-label', item.product_title || item.title || 'View product');
    if (item.image) {
      const image = doc.createElement('img');
      image.src = item.image;
      image.alt = item.product_title || item.title || '';
      image.loading = 'lazy';
      link.appendChild(image);
    }

    const details = doc.createElement('div');
    const label = doc.createElement('span');
    label.textContent = `ITEM / ${String(index + 1).padStart(2, '0')}`;
    const title = doc.createElement('a');
    title.href = item.url || '/cart';
    title.textContent = item.product_title || item.title || 'Product';
    const variant = doc.createElement('small');
    variant.textContent = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : `Quantity ${item.quantity || 1}`;
    const properties = doc.createElement('small');
    const propertyPairs = Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_'));
    if (propertyPairs.length) {
      properties.className = 'ro-cart-line__properties';
      properties.textContent = propertyPairs.map(([key, value]) => `${key}: ${value}`).join(' · ');
    }
    const price = doc.createElement('strong');
    price.textContent = formatMoney(item.final_line_price ?? item.line_price ?? 0, item.currency);
    const remove = doc.createElement('button');
    remove.type = 'button';
    remove.className = 'ro-cart-remove';
    remove.dataset.cartRemove = item.key || '';
    remove.setAttribute('aria-label', `Remove ${item.product_title || item.title || 'item'} from cart`);
    remove.textContent = 'Remove';
    remove.disabled = !item.key;
    details.append(label, title, variant);
    if (propertyPairs.length) details.appendChild(properties);
    details.append(price, remove);
    article.append(link, details);
    return article;
  };

  const renderCart = (cart) => {
    if (!cart) return;
    const count = Number(cart.item_count || 0);
    if (headerCount) {
      headerCount.textContent = String(count);
      headerCount.hidden = count === 0;
    }
    if (cartCountText) cartCountText.textContent = String(count);
    if (cartTotal) cartTotal.textContent = formatMoney(cart.items_subtotal_price ?? cart.total_price ?? 0, cart.currency);

    if (cartBody) {
      cartBody.replaceChildren();
      if (!Array.isArray(cart.items) || cart.items.length === 0) {
        const empty = doc.createElement('div');
        empty.className = 'ro-empty-cart';
        const eyebrow = doc.createElement('span');
        eyebrow.textContent = 'YOUR NEXT FAVOURITE PIECE';
        const heading = doc.createElement('h3');
        heading.append('Your bag ', doc.createElement('br'));
        const italic = doc.createElement('i');
        italic.textContent = 'is empty.';
        heading.appendChild(italic);
        const link = doc.createElement('a');
        link.className = 'ro-btn ro-btn--dark';
        const storefrontRoot = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
        link.href = `${storefrontRoot}#new-drops`;
        link.textContent = 'Explore collection →';
        empty.append(eyebrow, heading, link);
        cartBody.appendChild(empty);
      } else {
        const fragment = doc.createDocumentFragment();
        cart.items.forEach((item, index) => fragment.appendChild(createCartLine(item, index)));
        cartBody.appendChild(fragment);
      }

    }
    doc.dispatchEvent(new CustomEvent('remainingone:cart-updated', { detail: { cart } }));
  };

  if (cartBody) {
    cartBody.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-cart-remove]');
      if (!button || button.disabled) return;
      const key = button.dataset.cartRemove;
      if (!key) return;
      button.disabled = true;
      button.textContent = 'Removing…';
      try {
        const routeRoot = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
        const response = await fetch(`${routeRoot}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || 'Could not remove this item.');
        renderCart(result);
      } catch (error) {
        button.disabled = false;
        button.textContent = 'Remove';
        let errorNotice = cartBody.querySelector('[data-cart-notice]');
        if (!errorNotice) {
          errorNotice = doc.createElement('p');
          errorNotice.className = 'ro-cart-notice';
          errorNotice.dataset.cartNotice = '';
          errorNotice.setAttribute('role', 'status');
          cartBody.prepend(errorNotice);
        }
        errorNotice.textContent = error.message || 'Could not update the cart. Please try again.';
      }
    });
  }


  // Cart page remove controls use Shopify's cart Ajax endpoint, then refresh the rendered page.
  doc.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-page-cart-remove]');
    if (!button) return;
    event.preventDefault();
    const key = button.dataset.pageCartRemove;
    if (!key) return;
    button.disabled = true;
    button.textContent = 'Removing…';
    try {
      const routeRoot = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
      const response = await fetch(`${routeRoot}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.description || 'Could not remove this item.');
      win.location.reload();
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Remove';
      const notice = button.closest('.ro-cart-items')?.querySelector('[data-page-cart-error]');
      if (notice) {
        notice.hidden = false;
        notice.textContent = error.message || 'Could not update the cart. Please try again.';
      }
    }
  });

  const getCart = async () => {
    const routeRoot = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
    const response = await fetch(`${routeRoot}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not refresh cart.');
    return response.json();
  };

  const showCartError = (form, message) => {
    const error = form.querySelector('[data-cart-error]');
    if (error) {
      error.textContent = message || 'Something went wrong. Please try again.';
      error.hidden = false;
    }
  };

  const clearCartError = (form) => {
    const error = form.querySelector('[data-cart-error]');
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  };

  const productForm = doc.querySelector('#RemainingOneProductForm');
  if (productForm) {
    productForm.addEventListener('submit', async (event) => {
      if (!win.fetch) return;
      event.preventDefault();
      clearCartError(productForm);

      const requiredSize = productForm.querySelector('[data-property-input="Size"]');
      if (requiredSize && !requiredSize.value) {
        const sizeError = productForm.querySelector('[data-size-error]');
        if (sizeError) sizeError.hidden = false;
        const firstSize = productForm.querySelector('[data-property-name="Size"]');
        if (firstSize) firstSize.focus();
        return;
      }

      const submitButton = productForm.querySelector('[data-buy-button]');
      const originalText = submitButton ? submitButton.childNodes[0].nodeValue : '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.classList.add('is-loading');
        if (submitButton.firstChild) submitButton.firstChild.nodeValue = 'Adding… ';
      }

      try {
        const routeRoot = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
        const response = await fetch(`${routeRoot}cart/add.js`, {
          method: 'POST',
          body: new FormData(productForm),
          headers: { Accept: 'application/json' }
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'This item could not be added to your bag.');
        const cart = await getCart();
        renderCart(cart);
        setCart(true);
      } catch (error) {
        showCartError(productForm, error.message);
      } finally {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.classList.remove('is-loading');
          if (submitButton.firstChild) submitButton.firstChild.nodeValue = originalText || 'Add to bag ';
          if (submitButton.querySelector('span')) submitButton.querySelector('span').textContent = '→';
        }
      }
    });
  }

  if (cartShell && cartDrawer) {
    cartDrawer.inert = true;
    doc.querySelectorAll('[data-cart-open]').forEach((control) => control.addEventListener('click', (event) => { if (cartShell && cartDrawer) { event.preventDefault(); setCart(true); } }));
    doc.querySelectorAll('[data-cart-close]').forEach((button) => button.addEventListener('click', () => setCart(false)));
    cartDrawer.querySelectorAll('a[href*="/checkout"]').forEach((link) => link.addEventListener('click', () => setCart(false)));
    cartDrawer.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const nodes = Array.from(cartDrawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((node) => !node.hidden && !node.closest('[hidden]'));
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  /* Variant selection: only real Shopify variants change the submitted variant ID and price. */
  const initProductOptions = (form) => {
    const variantsElement = form.querySelector('[data-product-variants]');
    const idInput = form.querySelector('[data-variant-id]');
    const priceElement = form.querySelector('[data-style-price]');
    const buyButton = form.querySelector('[data-buy-button]');
    const qtyInput = form.querySelector('.ro-qty input');
    const productPage = form.closest('.ro-product-page');
    const mainImage = productPage ? productPage.querySelector('[data-product-main-image]') : null;
    const productInfo = form.closest('.ro-product-info');
    const mainPriceDisplay = productInfo?.querySelector('[data-price-display]');
    const stockStatusDisplay = productInfo?.querySelector('[data-stock-status]');
    const interestPanel = productInfo?.querySelector('[data-product-interest]');
    const reservationRequest = productInfo?.querySelector('[data-reservation-request]');
    const reservationVariant = productInfo?.querySelector('[data-reservation-variant]');
    const reservationSize = productInfo?.querySelector('[data-reservation-size]');
    const reservationColor = productInfo?.querySelector('[data-reservation-color]');
    const groups = Array.from(form.querySelectorAll('[data-option-group]'));
    let variants = [];
    try {
      variants = JSON.parse(variantsElement ? variantsElement.textContent : '[]');
    } catch (_) {
      variants = [];
    }

    const optionSelection = groups.map((group) => {
      const active = group.querySelector('[data-option-value].is-active') || group.querySelector('[data-option-value][aria-pressed="true"]');
      return active ? active.dataset.optionValue : '';
    });

    const updateGroups = () => {
      groups.forEach((group, index) => {
        const groupButtons = Array.from(group.querySelectorAll('[data-option-value]'));
        groupButtons.forEach((button) => {
          const active = button.dataset.optionValue === optionSelection[index];
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
          const possible = variants.some((variant) => variant.options[index] === button.dataset.optionValue && variant.available);
          button.disabled = !possible;
        });
      });
    };

    const updateVariant = (changedIndex = -1, changedValue = '') => {
      if (!variants.length || !groups.length) return;
      if (changedIndex >= 0) optionSelection[changedIndex] = changedValue;
      let variant = variants.find((candidate) => candidate.options.every((value, index) => value === optionSelection[index]));

      if (!variant && changedIndex >= 0) {
        const candidates = variants.filter((candidate) => candidate.options[changedIndex] === changedValue);
        candidates.sort((a, b) => {
          const score = (candidate) => candidate.options.reduce((total, value, index) => total + (index !== changedIndex && value === optionSelection[index] ? 1 : 0), 0);
          return score(b) - score(a) || Number(b.available) - Number(a.available);
        });
        variant = candidates[0];
        if (variant) variant.options.forEach((value, index) => { optionSelection[index] = value; });
      }

      updateGroups();
      if (!variant) {
        if (buyButton) buyButton.disabled = true;
        return;
      }
      if (idInput) idInput.value = variant.id;
      const formattedVariantPrice = variant.formatted_price || formatMoney(variant.price, body.dataset.storeCurrency);
      if (priceElement) priceElement.textContent = formattedVariantPrice;
      if (mainPriceDisplay) mainPriceDisplay.textContent = formattedVariantPrice;
      if (stockStatusDisplay) stockStatusDisplay.textContent = variant.available ? 'AVAILABLE NOW' : 'CURRENTLY UNAVAILABLE';
      if (buyButton) {
        buyButton.disabled = !variant.available;
        const textNode = Array.from(buyButton.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
        if (textNode) textNode.nodeValue = variant.available ? 'Add to bag ' : 'Sold out ';
      }
      if (interestPanel) interestPanel.hidden = Boolean(variant.available);
      if (reservationRequest) reservationRequest.hidden = !variant.available;
      if (reservationSize || reservationColor || reservationVariant) {
        let selectedSize = form.querySelector('[data-property-input="Size"]')?.value || '';
        let selectedColor = form.querySelector('[data-property-input="Color"]')?.value || '';
        groups.forEach((group, index) => {
          const name = (group.dataset.optionName || '').toLowerCase();
          if (name.includes('size')) selectedSize = optionSelection[index] || '';
          if (name.includes('color') || name.includes('colour')) selectedColor = optionSelection[index] || '';
        });
        if (reservationSize) reservationSize.value = selectedSize;
        if (reservationColor) reservationColor.value = selectedColor;
        if (reservationVariant) {
          reservationVariant.value = groups.length ? optionSelection.join(' / ') : [selectedSize && `Size: ${selectedSize}`, selectedColor && `Color: ${selectedColor}`].filter(Boolean).join(' / ') || variant.title;
        }
      }
      if (qtyInput) {
        const trackedAndLimited = Boolean(variant.inventory_management) && variant.inventory_policy === 'deny';
        const inventory = Number(variant.inventory_quantity);
        const max = trackedAndLimited && inventory > 0 ? inventory : 99;
        qtyInput.max = String(max);
        qtyInput.value = String(Math.min(max, Math.max(1, Number(qtyInput.value) || 1)));
      }
      if (variant.image && mainImage) {
        mainImage.src = variant.image;
        mainImage.alt = variant.image_alt || '';
        if (variant.image_id) mainImage.dataset.imageId = String(variant.image_id);
        if (productPage) {
          productPage.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
            const active = String(thumb.dataset.imageId) === String(variant.image_id);
            thumb.classList.toggle('is-active', active);
            thumb.setAttribute('aria-pressed', String(active));
          });
        }
      }
      if (changedIndex >= 0 && win.history && win.history.replaceState) {
        const url = new URL(win.location.href);
        url.searchParams.set('variant', variant.id);
        win.history.replaceState({}, '', url.toString());
      }
    };

    groups.forEach((group, index) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.addEventListener('click', () => updateVariant(index, button.dataset.optionValue));
      });
    });
    updateGroups();
    updateVariant();
  };

  doc.querySelectorAll('form.ro-product-form').forEach(initProductOptions);

  /* Custom line-item options for stores that have not yet configured Shopify variants. */
  doc.querySelectorAll('form.ro-product-form [data-property-name]').forEach((button) => {
    button.addEventListener('click', () => {
      const form = button.closest('form');
      const propertyName = button.dataset.propertyName;
      const propertyValue = button.dataset.propertyValue;
      const hiddenInput = form.querySelector(`[data-property-input="${CSS.escape(propertyName)}"]`);
      if (hiddenInput) hiddenInput.value = propertyValue;
      form.querySelectorAll(`[data-property-name="${CSS.escape(propertyName)}"]`).forEach((optionButton) => {
        const active = optionButton === button;
        optionButton.classList.toggle('is-active', active);
        optionButton.setAttribute('aria-pressed', String(active));
      });
      const productInfo = form.closest('.ro-product-info');
      const reservationField = productInfo && productInfo.querySelector(`[data-reservation-${propertyName.toLowerCase()}]`);
      if (reservationField) reservationField.value = propertyValue;
      const reservationVariant = productInfo && productInfo.querySelector('[data-reservation-variant]');
      if (reservationVariant && !form.querySelector('[data-option-group]')) {
        const sizeValue = form.querySelector('[data-property-input="Size"]')?.value || '';
        const colorValue = form.querySelector('[data-property-input="Color"]')?.value || '';
        reservationVariant.value = [sizeValue && `Size: ${sizeValue}`, colorValue && `Color: ${colorValue}`].filter(Boolean).join(' / ') || 'Standard product';
      }
      if (propertyName === 'Size') {
        const sizeError = form.querySelector('[data-size-error]');
        if (sizeError) sizeError.hidden = true;
      }
      clearCartError(form);
    });
  });

  /* Quantity, image thumbnails, and small interaction safeguards */
  doc.querySelectorAll('.ro-product-page').forEach((productPage) => {
    const qty = productPage.querySelector('.ro-qty input');
    const minus = productPage.querySelector('[data-minus]');
    const plus = productPage.querySelector('[data-plus]');
    if (qty) {
      const clampQty = () => {
        const min = Number(qty.min || 1);
        const max = Number(qty.max || 99);
        const value = Number(qty.value);
        qty.value = String(Math.min(max, Math.max(min, Number.isFinite(value) ? value : min)));
      };
      qty.addEventListener('change', clampQty);
      if (minus) minus.addEventListener('click', () => { qty.value = String(Math.max(Number(qty.min || 1), Number(qty.value || 1) - 1)); });
      if (plus) plus.addEventListener('click', () => { qty.value = String(Math.min(Number(qty.max || 99), Number(qty.value || 1) + 1)); });
    }

    const mainImage = productPage.querySelector('.ro-product-main-image img');
    productPage.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (mainImage) {
          mainImage.src = thumb.dataset.url;
          mainImage.alt = thumb.dataset.alt || '';
          mainImage.dataset.imageId = thumb.dataset.imageId || '';
        }
        productPage.querySelectorAll('[data-product-thumb]').forEach((item) => {
          const active = item === thumb;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
      });
    });
  });

  /* Product carousels: native horizontal scrolling with one-card arrow steps. */
  doc.querySelectorAll('.ro-home-carousel').forEach((carousel) => {
    const track = carousel.querySelector('[data-carousel-track]');
    if (!track) return;
    carousel.querySelectorAll('[data-carousel-control]').forEach((button) => {
      button.addEventListener('click', () => {
        const firstCard = track.querySelector('.ro-home-product-card');
        if (!firstCard) return;
        const direction = button.dataset.carouselControl === 'next' ? 1 : -1;
        const trackStyles = win.getComputedStyle(track);
        const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;
        const step = firstCard.getBoundingClientRect().width + gap;
        track.scrollBy({ left: step * direction, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });
  });

  /* Progressive enhancement only: content remains visible if animation APIs fail or motion is reduced. */
  const revealItems = new Set(doc.querySelectorAll('.ro-reveal'));
  const revealSelectors = [
    '.ro-home-feature__image', '.ro-home-feature__copy', '.ro-story-rule', '.ro-impact-details', '.ro-collection-intro > div',
    '.ro-species-tile', '.ro-final-cta > div', '.ro-story-hero__copy', '.ro-story-hero__media', '.ro-impact-hero__copy',
    '.ro-story-section-head', '.ro-story-principle', '.ro-story-process__visual', '.ro-story-process__copy', '.ro-story-process__step', '.ro-story-ending',
    '.ro-impact-standard__head', '.ro-impact-standard__item', '.ro-impact-proof__body > *', '.ro-impact-next',
    '.ro-contact-hero__intro', '.ro-contact-hero__aside', '.ro-contact-sidebar__head', '.ro-contact-topic', '.ro-contact-form-panel',
    '.ro-product-info', '.ro-product-story > *', '.ro-simple-page__head', '.ro-generic__head', '.ro-search-card',
    '.ro-home-divider__content', '.ro-home-carousel__head', '.ro-home-product-card', '.ro-release-card', '.ro-drop-card',
    '.ro-idea__headline', '.ro-idea__rule', '.ro-idea__lower > *',
    '.ro-reservation__intro > *', '.ro-reservation__step', '.ro-waitlist-signup__copy',
    '.ro-waitlist-signup__form', '.ro-reservation__bottom > *', '.ro-statement__grid > *',
    '.ro-footer__brand-block', '.ro-footer__column', '.ro-footer__meta', '.ro-footer__bottom',
    '.ro-contact-form-panel__head', '.ro-contact-form__row', '.ro-contact-form__submit'
  ];
  doc.querySelectorAll(revealSelectors.join(',')).forEach((item) => {
    item.classList.add('ro-reveal');
    revealItems.add(item);
  });
  // Card reveals are staggered by a small amount, capped so large collections don't feel sluggish.
  const staggerGroups = ['.ro-species-grid', '.ro-search-grid', '.ro-home-carousel__track', '.ro-release-grid', '.ro-drops-grid', '.ro-footer__top', '.ro-story-principle-grid', '.ro-idea__lower', '.ro-reservation__steps', '.ro-reservation__bottom'];
  staggerGroups.forEach((selector) => {
    doc.querySelectorAll(selector).forEach((group) => {
      Array.from(group.children).filter((item) => item.classList.contains('ro-reveal')).forEach((item, index) => {
        item.style.setProperty('--ro-reveal-delay', `${Math.min(index, 7) * 55}ms`);
      });
    });
  });
  if (reduceMotion || !('IntersectionObserver' in win)) {
    revealItems.forEach((item) => item.classList.add('is-in'));
  } else if (revealItems.size) {
    root.classList.add('ro-motion-ready');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -4% 0px' });
    revealItems.forEach((item) => observer.observe(item));
  }

  win.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setMenu(false);
      setCart(false);
      setSearch(false);
    }
  });
})();
